/**
 * Child Safety Standards — public page (no auth required).
 *
 * Google Play requires an in-app link + a public URL that documents our
 * zero-tolerance CSAE / CSAM policy and the designated Child-Safety Point
 * of Contact. This route serves that document at /child-safety.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, ChevronLeft, AlertTriangle } from 'lucide-react';
import Logo from '../components/Logo';

const EFFECTIVE_DATE = '15 August 2026';
const CSAE_CONTACT = 'sarvaritam@gmail.com';

export default function ChildSafety() {
  return (
    <div className="min-h-screen cosmic-bg relative pb-20">
      <div className="stars-bg" />
      {/* Header */}
      <header className="relative border-b border-white/8 bg-black/30 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          <Link
            to="/"
            data-testid="child-safety-back"
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition"
            aria-label="Back to home"
          >
            <ChevronLeft size={18} />
          </Link>
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <Logo size="sm" />
            <span className="font-display font-bold text-lg hidden sm:inline">WorldVibe</span>
          </Link>
          <span className="ml-auto text-[10px] uppercase tracking-[0.22em] font-semibold text-emerald-300 flex items-center gap-1">
            <Shield size={12} /> Child Safety
          </span>
        </div>
      </header>

      <main className="relative max-w-3xl mx-auto px-5 pt-8 space-y-8" data-testid="child-safety-page">
        {/* Hero */}
        <section
          className="relative overflow-hidden rounded-3xl border border-white/12 p-6 sm:p-8"
          style={{
            background:
              'radial-gradient(circle at 15% 0%, rgba(20,184,166,0.28), transparent 55%),' +
              'radial-gradient(circle at 100% 100%, rgba(59,130,246,0.24), transparent 60%),' +
              '#0F0F1B',
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-300 font-semibold flex items-center gap-1">
            <Shield size={12} /> WorldVibe Child Safety Standards
          </div>
          <h1 className="mt-3 font-display font-bold text-3xl sm:text-4xl leading-tight">
            Zero tolerance for child abuse or exploitation.
          </h1>
          <p className="mt-3 text-white/70 text-sm leading-relaxed">
            <b>Effective Date:</b> {EFFECTIVE_DATE}. WorldVibe, operated by <b>SarvaRitam Businesses LLP</b>, is
            committed to keeping every traveler safe. We prohibit all forms of Child Sexual Abuse and
            Exploitation (<b>CSAE</b>) and Child Sexual Abuse Material (<b>CSAM</b>) on this platform.
          </p>
        </section>

        {/* Sections */}
        <Section n="1" title="Prohibition of Child Sexual Abuse and Exploitation">
          <p>WorldVibe strictly prohibits all forms of Child Sexual Abuse and Exploitation (CSAE), including:</p>
          <ul>
            <li>Child sexual abuse or exploitation</li>
            <li>Child Sexual Abuse Material (CSAM)</li>
            <li>Child grooming for sexual purposes</li>
            <li>Sexual solicitation or exploitation of minors</li>
            <li>Sextortion involving a child</li>
            <li>Trafficking or commercial sexual exploitation of children</li>
            <li>Creating, uploading, requesting, sharing, distributing, or promoting sexually exploitative content involving minors</li>
            <li>Any other behavior that sexually exploits, abuses, or endangers children</li>
          </ul>
          <p>
            Users must not use WorldVibe to create, upload, request, store, share, distribute, or promote any
            content or activity that facilitates the sexual abuse or exploitation of children.
          </p>
        </Section>

        <Section n="2" title="Zero-Tolerance Policy">
          <p>
            WorldVibe maintains a <b>zero-tolerance</b> approach toward CSAE and CSAM. If we identify or receive
            a report regarding content or behavior that violates these standards, WorldVibe may take immediate
            action, including:
          </p>
          <ul>
            <li>Removing or restricting reported content</li>
            <li>Suspending the user account</li>
            <li>Permanently terminating the user account</li>
            <li>Blocking users from accessing WorldVibe</li>
            <li>Preserving relevant information where legally required</li>
            <li>Reporting confirmed illegal content or activity to the appropriate authorities as required by applicable law</li>
          </ul>
        </Section>

        <Section n="3" title="Reporting Child Safety Concerns">
          <p>
            Users can report child-safety concerns through the reporting and feedback mechanisms available inside
            the WorldVibe app — tap the <b>flag</b> icon on any user profile, planet message, DM, or feed post and
            choose <b>“Child Sexual Abuse (CSAE / CSAM)”</b> as the reason. These reports jump to the top of our
            queue and are emailed immediately to our designated Child-Safety Point of Contact.
          </p>
          <p>
            Users should report any suspected CSAE, CSAM, child grooming, exploitation, or other child-safety
            concern as soon as possible. WorldVibe reviews submitted reports and takes appropriate action in
            accordance with these standards and applicable laws.
          </p>
        </Section>

        <Section n="4" title="Child Safety Point of Contact" highlight>
          <p>
            For child-safety concerns, CSAE/CSAM reports, or questions regarding WorldVibe’s child-safety
            practices, please contact our designated Child-Safety Point of Contact:
          </p>
          <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/10 p-4 mt-3">
            <div className="font-semibold text-white">SarvaRitam Businesses LLP</div>
            <div className="text-white/70 text-sm">Child Safety Point of Contact</div>
            <a
              href={`mailto:${CSAE_CONTACT}`}
              className="mt-2 inline-flex items-center gap-2 text-emerald-200 hover:text-white transition text-sm font-medium"
              data-testid="child-safety-contact-email"
            >
              <Mail size={14} /> {CSAE_CONTACT}
            </a>
          </div>
          <p className="mt-3 text-white/60 text-sm">
            This contact is responsible for receiving and addressing child-safety concerns and can communicate
            with relevant authorities or Google Play regarding child-safety matters when required.
          </p>
        </Section>

        <Section n="5" title="CSAM Handling">
          <p>
            WorldVibe does not permit the creation, upload, storage, distribution, or sharing of CSAM through its
            platform. When WorldVibe becomes aware of CSAM or other child sexual exploitation content, appropriate
            action will be taken, including removal or restriction of the content and account enforcement, in
            accordance with these standards and applicable laws.
          </p>
        </Section>

        <Section n="6" title="Compliance With Child Safety Laws">
          <p>
            WorldVibe is committed to complying with applicable child-safety laws and regulations. Where required
            by applicable law, confirmed CSAM or child exploitation incidents may be reported to appropriate
            law-enforcement or child-safety authorities.
          </p>
        </Section>

        <Section n="7" title="User Responsibility">
          <p>
            All WorldVibe users are responsible for using the platform in a lawful and safe manner. Users must not
            engage in, encourage, facilitate, or promote any activity that sexually exploits, abuses, or endangers
            children. Violations of these standards may result in immediate account suspension or termination and
            may be reported to appropriate authorities where required.
          </p>
        </Section>

        <Section n="8" title="Contact">
          <p>
            For child-safety-specific concerns, please contact:
          </p>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mt-2">
            <div className="font-semibold">SarvaRitam Businesses LLP</div>
            <div className="text-white/70 text-sm">Child Safety Point of Contact</div>
            <a
              href={`mailto:${CSAE_CONTACT}`}
              className="mt-2 inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 transition text-sm font-medium"
            >
              <Mail size={14} /> {CSAE_CONTACT}
            </a>
          </div>
          <p className="mt-3 text-white/60 text-sm">
            WorldVibe is committed to protecting children and maintaining a safe platform for all users.
          </p>
        </Section>

        {/* Emergency banner */}
        <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-red-300 mt-0.5 shrink-0" />
          <div className="text-sm text-white/85 leading-snug">
            <b>If a child is in immediate danger,</b> contact your local emergency services first, then let us know
            at <a href={`mailto:${CSAE_CONTACT}`} className="text-red-200 underline hover:text-white">{CSAE_CONTACT}</a>.
          </div>
        </div>

        <div className="text-center text-xs text-white/40 pt-2">
          © {new Date().getFullYear()} SarvaRitam Businesses LLP — WorldVibe. Last updated {EFFECTIVE_DATE}.
        </div>
      </main>
    </div>
  );
}

function Section({ n, title, children, highlight }) {
  return (
    <section
      className={`rounded-2xl p-5 sm:p-6 border ${highlight ? 'border-emerald-400/30 bg-emerald-500/[0.06]' : 'border-white/10 bg-[#0F0F1A]'}`}
      data-testid={`child-safety-section-${n}`}
    >
      <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-white/45">Section {n}</div>
      <h2 className="mt-1 font-display font-bold text-xl leading-tight">{title}</h2>
      <div className="mt-3 space-y-3 text-white/75 text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_b]:text-white">
        {children}
      </div>
    </section>
  );
}
